import React from 'react';
import classNames from 'classnames';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import {
  ComposableMap,
  ZoomableGroup,
  Geographies,
  Geography,
  Graticule,
  Marker,
} from 'react-simple-maps';

import { Modal, ModalStore } from 'ui/base/modal';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { useScreenSize } from 'ui/base/screen';
import { StandardPage } from 'ui/base/page/standard_page';

import { CountryCode, getLocalNameByCode } from 'services/base/countries';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { formatUnits } from 'services/base/format';

import { Pangolin } from 'services/accounts/types';
import { IPangolinDomain, DeviceType, History, HistoryLocation } from 'services/pangolin/types';
import { HostDeviceType } from 'services/pangolin/protos/hosts_pb';
import { hostDisplayName, hostDisplayDeviceType } from 'services/pangolin/hosts_service';

import {
  Action,
  fromBoolean,
  getFullAction,
} from 'assets/enums/actions/actions';

import {
  Direction,
  fromHistoryDirection,
  getFullDirection,
} from 'assets/enums/directions/directions';

import {
  NetworkProtocol,
  fromNetworkProtocolNumber,
  getShortNetworkProtocol,
  TransportProtocol,
  fromTransportProtocolNumber,
  getShortTransportProtocol,
} from 'assets/enums/protocols/protocols';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import Messages from './view_history_messages';
import baseStyles from './view_history.module.css';

import 'flag-icons/css/flag-icons.min.css';

import MAP_GEO_WORLD from 'assets/geo/world.json';
import MAP_GEO_US from 'assets/geo/countries/us.json';
import MAP_GEO_CN from 'assets/geo/countries/cn.json';
import MAP_GEO_IN from 'assets/geo/countries/in.json';
import MAP_GEO_CA from 'assets/geo/countries/ca.json';
import MAP_GEO_RU from 'assets/geo/countries/ru.json';
import MAP_GEO_IR from 'assets/geo/countries/ir.json';
import MAP_GEO_BR from 'assets/geo/countries/br.json';
import MAP_GEO_AU from 'assets/geo/countries/au.json';
import MAP_GEO_MX from 'assets/geo/countries/mx.json';
import MAP_GEO_ID from 'assets/geo/countries/id.json';
import MAP_GEO_SA from 'assets/geo/countries/sa.json';

// Maps of "AG20" countries
//
// That is, countries that fit the following criteria:
// * A = Area: land area is in the top 20
//     * A country must be physically big before we consider drawing its
//       internal regions. Drawing the internal borders of small countries
//       will give the map weird uneven divisions.
// * G = GDP: GDP is in the top 20
//     * A country must have a significant economy before we consider drawing
//       its internal regions as those countries are more likely to host data
//       centers, meaning, they are more likely appear in traffic history
//       entries.

const MAP_GEO_AG20 = new Map<CountryCode, object>([
  [CountryCode.US, MAP_GEO_US],
  [CountryCode.CN, MAP_GEO_CN],
  [CountryCode.IN, MAP_GEO_IN],
  [CountryCode.CA, MAP_GEO_CA],
  [CountryCode.RU, MAP_GEO_RU],
  [CountryCode.IR, MAP_GEO_IR],
  [CountryCode.BR, MAP_GEO_BR],
  [CountryCode.AU, MAP_GEO_AU],
  [CountryCode.MX, MAP_GEO_MX],
  [CountryCode.ID, MAP_GEO_ID],
  [CountryCode.SA, MAP_GEO_SA],
]);

const MAP_HEIGHT = 250;
const MAP_KNOWN_ZOOM = 3;
const MAP_UNKNOWN_ZOOM = 0.75;
const MAP_PROJECTION = 'geoMercator';

type ViewHistoryStyles = {
  modal: string;
  background: string;
  container: string;

  header: string;
  headerCover: string;
  headerCoverGradient: string;
  headerCoverDevices: string;
  headerContainer: string;
  headerBack: string;
  headerInfo: string;
  headerText: string;
  headerTextIcon: string;
  headerTextContainer: string;
  headerTextTitle: string;
  headerTextSubtitle: string;

  content: string;
  footer: string;
  divider: string,
  section: string;
  sectionTitle: string;
  row: string;
  column: string;
  label: string;
  value: string;

  hostNameContainer: string;
  hostNameIcon: string;
  hostNameText: string;

  deviceIcon: string;

  // map styling
  mapSvgGraticuleStrokeColor: string;
  mapSvgCountryStrokeColor: string;
  mapSvgCountryFillColor: string;
  mapSvgRegionStrokeColor: string;
  mapSvgRegionFillColor: string;
  mapSvgMarkerStrokeColor: string;
  mapSvgMarkerFillColor: string;
};

const themedStyles: ThemedStyles<ViewHistoryStyles> = {
  [Theme.DAYLIGHT]: {
    modal: baseStyles.modal,
    background: baseStyles.daylightBackground,
    container: baseStyles.container,

    header: baseStyles.header,
    headerCover: baseStyles.headerCover,
    headerCoverGradient: baseStyles.daylightHeaderCoverGradient,
    headerCoverDevices: baseStyles.daylightHeaderCoverDevices,
    headerContainer: baseStyles.headerContainer,
    headerBack: baseStyles.headerBack,
    headerInfo: baseStyles.headerInfo,
    headerText: baseStyles.headerText,
    headerTextIcon: baseStyles.headerTextIcon,
    headerTextContainer: baseStyles.headerTextContainer,
    headerTextTitle: baseStyles.headerTextTitle,
    headerTextSubtitle: baseStyles.headerTextSubtitle,

    content: baseStyles.content,
    footer: baseStyles.footer,
    divider: baseStyles.daylightDivider,
    section: baseStyles.section,
    sectionTitle: baseStyles.daylightSectionTitle,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.daylightLabel,
    value: baseStyles.value,

    hostNameContainer: baseStyles.hostNameContainer,
    hostNameIcon: baseStyles.hostNameIcon,
    hostNameText: baseStyles.hostNameText,

    deviceIcon: baseStyles.deviceIcon,

    // map styling
    mapSvgGraticuleStrokeColor: '#5151661a',
    mapSvgCountryStrokeColor: '#2d539dff',
    mapSvgCountryFillColor: '#2d539d80',
    mapSvgRegionStrokeColor: '#2d539dff',
    mapSvgRegionFillColor: '#00000000',
    mapSvgMarkerStrokeColor: '#9e2e49ff',
    mapSvgMarkerFillColor: '#9e2e4980',
  },
  [Theme.MIDNIGHT]: {
    modal: baseStyles.modal,
    background: baseStyles.midnightBackground,
    container: baseStyles.container,

    header: baseStyles.header,
    headerCover: baseStyles.headerCover,
    headerCoverGradient: baseStyles.midnightHeaderCoverGradient,
    headerCoverDevices: baseStyles.midnightHeaderCoverDevices,
    headerContainer: baseStyles.headerContainer,
    headerBack: baseStyles.headerBack,
    headerInfo: baseStyles.headerInfo,
    headerText: baseStyles.headerText,
    headerTextIcon: baseStyles.headerTextIcon,
    headerTextContainer: baseStyles.headerTextContainer,
    headerTextTitle: baseStyles.headerTextTitle,
    headerTextSubtitle: baseStyles.headerTextSubtitle,

    content: baseStyles.content,
    footer: baseStyles.footer,
    divider: baseStyles.midnightDivider,
    section: baseStyles.section,
    sectionTitle: baseStyles.midnightSectionTitle,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.midnightLabel,
    value: baseStyles.value,

    hostNameContainer: baseStyles.hostNameContainer,
    hostNameIcon: baseStyles.hostNameIcon,
    hostNameText: baseStyles.hostNameText,

    deviceIcon: baseStyles.deviceIcon,

    // map styling
    mapSvgGraticuleStrokeColor: '#dddef81a',
    mapSvgCountryStrokeColor: '#00cbfdff',
    mapSvgCountryFillColor: '#00cbfd80',
    mapSvgRegionStrokeColor: '#00cbfdff',
    mapSvgRegionFillColor: '#00000000',
    mapSvgMarkerStrokeColor: '#b282ffff',
    mapSvgMarkerFillColor: '#b282ff80',
  },
};

type ViewHistoryHeaderMapProps = {
  location: HistoryLocation | undefined;
};
const ViewHistoryHeaderMap = observer(({
  location,
}: ViewHistoryHeaderMapProps) => {
  const styles = useStyles(themedStyles);
  const [screenWidth] = useScreenSize();
  const mapCountry = (location?.country_short !== undefined ?
                      location.country_short as CountryCode : undefined);
  const mapGeoCountry = (mapCountry !== undefined ?
                         MAP_GEO_AG20.get(mapCountry) : undefined);
  const mapZoom = location !== undefined ? MAP_KNOWN_ZOOM : MAP_UNKNOWN_ZOOM;
  const mapLongitude = location !== undefined ? location.longitude : 0;
  const mapLatitude = location !== undefined ? location.latitude : 0;
  return (
    <div className={styles.headerCover} style={{ height: MAP_HEIGHT }}>
      <div className={styles.headerCoverGradient}></div>
      <ComposableMap projection={MAP_PROJECTION}
                     height={MAP_HEIGHT} width={screenWidth}
                     style={{ minHeight: MAP_HEIGHT, minWidth: screenWidth }}>
        <ZoomableGroup zoom={mapZoom} minZoom={mapZoom} maxZoom={mapZoom}
                       center={[mapLongitude, mapLatitude]}>
          <Graticule stroke={styles.mapSvgGraticuleStrokeColor} strokeWidth={0.5} />
          <Geographies geography={MAP_GEO_WORLD}>
            {
              ({ geographies }) =>
                geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo}
                             fill={styles.mapSvgCountryFillColor}
                             stroke={styles.mapSvgCountryStrokeColor}
                             strokeWidth={0.5} />
                ))
            }
          </Geographies>
          {
            mapGeoCountry !== undefined ?
            <Geographies geography={mapGeoCountry}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo}
                             fill={styles.mapSvgRegionFillColor}
                             stroke={styles.mapSvgRegionStrokeColor}
                             strokeWidth={0.1} />
                ))
              }
            </Geographies> : null
          }
          {
            location !== undefined ? (
              <Marker coordinates={[mapLongitude, mapLatitude]}>
                <circle fill={styles.mapSvgMarkerFillColor}
                        stroke={styles.mapSvgMarkerStrokeColor}
                        strokeWidth={0.5} r={1.5} />
              </Marker>
            ) : null
          }
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
});

type ViewHistoryHeaderDevicesProps = {
  type_src: DeviceType;
  type_dst: DeviceType;
};
const ViewHistoryHeaderDevices = observer(({
  type_src,
  type_dst,
}: ViewHistoryHeaderDevicesProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);

  const icon_src = getDeviceTypeIcon(deviceIcons, type_src);
  const icon_dst = getDeviceTypeIcon(deviceIcons, type_dst);

  return (
    <div className={styles.headerCover} style={{ height: MAP_HEIGHT }}>
      <div className={styles.headerCoverGradient}></div>
      <div className={styles.headerCoverDevices}>
        <span className={classNames(styles.deviceIcon, icon_src)}/>
        &nbsp;→&nbsp;
        <span className={classNames(styles.deviceIcon, icon_dst)}/>
      </div>
    </div>
  );
});

type ViewHistoryHeaderProps = {
  domain: IPangolinDomain;
  icon: React.ReactNode;
  name: string;
  address: string;
  network: NetworkProtocol | null;
  transport: TransportProtocol | null;
  port: number;
  locale: string;
  format: object;
  start: Date;
  children: React.ReactNode;
};
const ViewHistoryHeader = observer(({
  domain,
  icon,
  name,
  address,
  network,
  transport,
  port,
  locale,
  format,
  start,
  children,
}: ViewHistoryHeaderProps) => {
  const nav = useHistory();
  const styles = useStyles(themedStyles);

  const onHideModal = React.useCallback(action(() => {
    domain.historyItem = undefined;
    nav.goBack();
  }), []);

  return (
    <div className={styles.header} style={{ height: MAP_HEIGHT }}>
      {children}
      <div className={styles.headerContainer}>
        <div className={styles.headerBack}>
          <BackButton onClick={onHideModal}/>
        </div>
        <div className={styles.headerInfo}>
          <div className={styles.headerText}>
            <div className={styles.headerTextIcon}>
              {icon}
            </div>
            <div className={styles.headerTextContainer}>
              <div className={styles.headerTextTitle}>
                {name}
              </div>
              <div className={styles.headerTextSubtitle}>
                {getShortNetworkProtocol(network)} {address}, {getShortTransportProtocol(transport)} {port}
              </div>
              <div className={styles.headerTextSubtitle}>
                {start.toLocaleString(locale, format)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

type ViewHistorySummaryProps = {
  direction: Direction | null;
  allowed: Action | null;
  locale: string;
  format: object;
  start: Date;
  last: Date;
  network: NetworkProtocol | null;
  transport: TransportProtocol | null;
  bytes: number;
  packets: number;
};
const ViewHistorySummary = observer(({
  direction,
  allowed,
  locale,
  format,
  start,
  last,
  network,
  transport,
  bytes,
  packets,
}: ViewHistorySummaryProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.section}>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.directionLabel()}
          </div>
          <div className={styles.value}>
            {getFullDirection(direction)}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.actionLabel()}
          </div>
          <div className={styles.value}>
            {getFullAction(allowed)}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.startDateLabel()}
          </div>
          <div className={styles.value}>
            {start.toLocaleString(locale, format)}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.endDateLabel()}
          </div>
          <div className={styles.value}>
            {last.toLocaleString(locale, format)}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.networkProtocolLabel()}
          </div>
          <div className={styles.value}>
            {getShortNetworkProtocol(network)}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.transportProtocolLabel()}
          </div>
          <div className={styles.value}>
            {getShortTransportProtocol(transport)}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.totalBytesLabel()}
          </div>
          <div className={styles.value}>
            {formatUnits(bytes)}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.totalPacketsLabel()}
          </div>
          <div className={styles.value}>
            {formatUnits(packets)}
          </div>
        </div>
      </div>
    </div>
  );
});

type ViewHistoryInternalProps = {
  source: boolean;
  name: string;
  type: DeviceType;
  address: string;
  port: number;
  bytes: number;
  packets: number;
};
const ViewHistoryHostInternal = observer(({
  source,
  name,
  type,
  address,
  port,
  bytes,
  packets,
}: ViewHistoryInternalProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = getDeviceTypeIcon(deviceIcons, type);
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {source ? Messages.sourceLabel() : Messages.destinationLabel()}
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={classNames(styles.value, styles.hostNameContainer)}>
            <div className={styles.hostNameIcon}>
              <span className={classNames(styles.deviceIcon, deviceIconClass)}/>
            </div>
            <div className={styles.hostNameText}>
              {name}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.ipAddressLabel()}
          </div>
          <div className={styles.value}>
            {address}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.portNumberLabel()}
          </div>
          <div className={styles.value}>
            {port}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.sentBytesLabel()}
          </div>
          <div className={styles.value}>
            {formatUnits(bytes)}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.sentPacketsLabel()}
          </div>
          <div className={styles.value}>
            {formatUnits(packets)}
          </div>
        </div>
      </div>
    </div>
  );
});

type ViewHistoryExternalProps = {
  source: boolean;
  name: string;
  address: string;
  port: number;
  bytes: number;
  packets: number;
  location: HistoryLocation | undefined;
  flag: string;
  domains: string[] | undefined;
};
const ViewHistoryHostExternal = observer(({
  source,
  name,
  address,
  port,
  bytes,
  packets,
  location,
  flag,
  domains,
}: ViewHistoryExternalProps) => {
  const styles = useStyles(themedStyles);
  const countryCode = flag as CountryCode;
  const countryName = getLocalNameByCode(countryCode);
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {source ? Messages.sourceLabel() : Messages.destinationLabel()}
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={classNames(styles.value, styles.hostNameContainer)}>
            <div className={styles.hostNameIcon}>
              <span className={classNames('fi', `fi-${flag.toLowerCase()}`)}/>
            </div>
            <div className={styles.hostNameText}>
              {name}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.ipAddressLabel()}
          </div>
          <div className={styles.value}>
            {address}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.portNumberLabel()}
          </div>
          <div className={styles.value}>
            {port}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.sentBytesLabel()}
          </div>
          <div className={styles.value}>
            {formatUnits(bytes)}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.sentPacketsLabel()}
          </div>
          <div className={styles.value}>
            {formatUnits(packets)}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.countryLabel()}
          </div>
          <div className={styles.value}>
            {countryName !== undefined ? countryName : '-'}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.regionLabel()}
          </div>
          <div className={styles.value}>
            {location !== undefined ? location.region : '-'}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.cityLabel()}
          </div>
          <div className={styles.value}>
            {location !== undefined ? location.city : '-'}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.zipCodeLabel()}
          </div>
          <div className={styles.value}>
            {location !== undefined ? location.zipcode : '-'}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.longtitudeLabel()}
          </div>
          <div className={styles.value}>
            {location !== undefined ? location.longitude : '-'}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.latitudeLabel()}
          </div>
          <div className={styles.value}>
            {location !== undefined ? location.latitude : '-'}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.timezoneLabel()}
          </div>
          <div className={styles.value}>
            {location !== undefined ? location.timezone : '-'}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.label}>
            {Messages.domainsLabel()}
          </div>
          <div className={styles.value}>
            {domains?.length ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
});

type ViewHistoryMainProps = {
  domain: IPangolinDomain;
  history: History;
};
const ViewHistoryMain = observer(({
  domain,
  history,
}: ViewHistoryMainProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);

  const allowed = fromBoolean(history.allowed);
  const direction = fromHistoryDirection(history.direction);
  const network = fromNetworkProtocolNumber(history.proto_l3);
  const transport = fromTransportProtocolNumber(history.proto_l4);

  const inbound = direction === Direction.INBOUND;

  const external_src = direction === Direction.INBOUND;
  const external_dst = direction === Direction.OUTBOUND;

  const addr_src = history.addr_src;
  const addr_dst = history.addr_dst;
  const addr = inbound ? addr_src : addr_dst;

  const port_src = history.port_src;
  const port_dst = history.port_dst;
  const port = inbound ? port_src : port_dst;

  const host_src = domain.hosts.getMac(history.mac_src);
  const host_dst = domain.hosts.getMac(history.mac_dst);
  const host = inbound ? host_src : host_dst;

  const domains_src = history.domains_src;
  const domains_dst = history.domains_dst;

  const name_src = host_src ? (hostDisplayName(host_src) ?? host_src.mac ?? host_src.ip) :
                   (domains_src?.length ? domains_src[0] : history.addr_src);
  const name_dst = host_dst ? (hostDisplayName(host_dst) ?? host_dst.mac ?? host_dst.ip) :
                   (domains_dst?.length ? domains_dst[0] : history.addr_dst);
  const name = inbound ? name_src : name_dst;

  const type_src = host_src ? hostDisplayDeviceType(host_src) : HostDeviceType.H_UNKNOWN;
  const type_dst = host_dst ? hostDisplayDeviceType(host_dst) : HostDeviceType.H_UNKNOWN;
  const type = inbound ? type_src : type_dst;

  const location_src = history.location_src;
  const location_dst = history.location_dst;
  const location = inbound ? location_src : location_dst;

  const flag_src = location_src?.country_short ?? 'XX';
  const flag_dst = location_dst?.country_short ?? 'XX';
  const flag_main = inbound ? flag_src : flag_dst;

  const icon_cls = getDeviceTypeIcon(deviceIcons, type);
  const icon = host ? <span className={classNames(styles.deviceIcon, icon_cls)}/> :
               <span className={classNames('fi', `fi-${flag_main.toLowerCase()}`)}/>;

  const start = new Date(0); start.setUTCSeconds(history.start);
  const last = new Date(0); last.setUTCSeconds(history.last);
  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'numeric',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <ViewHistoryHeader
          domain={domain}
          name={name}
          address={addr}
          network={network}
          transport={transport}
          port={port}
          locale={locale}
          format={format}
          start={start}
          icon={icon}
        >
          {
            direction !== Direction.LOCAL ?
              <ViewHistoryHeaderMap
                location={location}
              /> :
              <ViewHistoryHeaderDevices
                type_src={type_src}
                type_dst={type_dst}
              />
          }
        </ViewHistoryHeader>
        <div className={styles.content}>
          <ViewHistorySummary
            direction={direction}
            allowed={allowed}
            locale={locale}
            format={format}
            start={start}
            last={last}
            network={network}
            transport={transport}
            bytes={history.bytes_orig + history.bytes_repl}
            packets={history.pkts_orig + history.pkts_repl}
          />
          <div className={styles.divider} />
          {
            external_src ?
            <ViewHistoryHostExternal
              source={true}
              name={name_src}
              address={addr_src}
              port={port_src}
              bytes={history.bytes_orig}
              packets={history.pkts_orig}
              location={location_src}
              flag={flag_src}
              domains={domains_src}
            /> :
            <ViewHistoryHostInternal
              source={true}
              name={name_src}
              type={type_src}
              address={addr_src}
              port={port_src}
              bytes={history.bytes_orig}
              packets={history.pkts_orig}
            />
          }
          <div className={styles.divider} />
          {
            external_dst ?
            <ViewHistoryHostExternal
              source={false}
              name={name_dst}
              address={addr_dst}
              port={port_dst}
              bytes={history.bytes_repl}
              packets={history.pkts_repl}
              location={location_dst}
              flag={flag_dst}
              domains={domains_dst}
            /> :
            <ViewHistoryHostInternal
              source={false}
              name={name_dst}
              type={type_dst}
              address={addr_dst}
              port={port_dst}
              bytes={history.bytes_repl}
              packets={history.pkts_repl}
            />
          }
        </div>
        <div className={styles.footer}>
          <p>{Messages.creditIP2Location()}</p>
          <p>{Messages.creditCartographyVectors()}</p>
        </div>
      </div>
    </div>
  );
});

type BaseViewHistoryProps = {
  domain: IPangolinDomain;
  pangolin: Pangolin;
};
export const BaseViewHistory = observer(({
  domain,
}: BaseViewHistoryProps) => {
  const history = domain.historyItem;
  return history === undefined ? (
    <StandardPage>
    </StandardPage>
  ) : (
    <ViewHistoryMain
      domain={domain}
      history={history}
    />
  );
});

type ViewHistoryModalProps = {
  domain: IPangolinDomain;
  pangolin: Pangolin;
  modal: ModalStore;
};
export const ViewHistoryModal = observer(({
  domain,
  pangolin,
  modal,
}: ViewHistoryModalProps) => {
  const styles = useStyles(themedStyles);
  return (
    <Modal store={modal}>
      <div className={styles.modal}>
        {
          modal.visible &&
          <BaseViewHistory
            domain={domain}
            pangolin={pangolin}
          />
        }
      </div>
    </Modal>
  );
});
